import type { ChangeEvent, KeyboardEvent } from "react";
import { useRef, useState } from "react";

import type { TextFieldProps } from "@braintrust/braintrust-ui-components";
import {
  IconButton,
  InputAdornment,
  TextField,
} from "@braintrust/braintrust-ui-components";
import { AddCircleOutlineIcon } from "@braintrust/braintrust-ui-components/Icons";

type AddCriteriaButtonProps = {
  onAdd: (value: string) => void;
  iconButtonAriaLabel: string;
} & Pick<TextFieldProps, "id" | "placeholder">;

export const AddItemTextField = ({
  onAdd,
  iconButtonAriaLabel,
  ...rest
}: AddCriteriaButtonProps) => {
  const [value, setValue] = useState("");
  const ref = useRef<HTMLInputElement>(null);

  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    setValue(ev.target.value);
  };

  const handleAdd = () => {
    if (!value) {
      return;
    }
    setValue("");
    onAdd(value);
  };

  const handleKeyDown = (ev: KeyboardEvent) => {
    ev.stopPropagation();
    if (ev.key !== "Enter") {
      return;
    }

    ev.preventDefault();
    ref.current?.blur();
    handleAdd();
  };

  return (
    <TextField
      {...rest}
      inputRef={ref}
      onChange={handleChange}
      value={value}
      onKeyDown={handleKeyDown}
      sx={{
        input: { pl: "0px !important" },
        ".MuiOutlinedInput-root:not(.Mui-focused)": {
          "&:not(:hover) fieldset": {
            borderColor: "var(--grey-3)",
          },
          "&:hover fieldset": {
            borderColor: "var(--grey-1)",
          },
        },
        ".MuiOutlinedInput-notchedOutline": {
          borderStyle: "dashed",
        },
      }}
      slotProps={{
        input: {
          sx: { pl: "8px !important" },
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5 }}>
              <IconButton
                aria-label={iconButtonAriaLabel}
                variant="transparent"
                onClick={handleAdd}
                sx={{ p: "8px !important" }}
              >
                <AddCircleOutlineIcon
                  sx={{ color: !!value ? "inherit" : "var(--grey-3)" }}
                />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
